/**
 * User Authentication Page
 * @description 사용자 인증 관리 페이지
 */

import { useState, useEffect } from 'react';
import './tenant-admin.css';
import {
  Badge, Button, Card, Checkbox, Dialog, DialogContent, DialogDescription,
  DialogHeader, DialogTitle, DialogTrigger, Input, Label, Select, SelectContent,
  SelectItem, SelectTrigger, SelectValue
} from '@shared/ui/primitives';
import { getQueryParam, removeQueryParams } from '@shared/router';
import { useAuthStore } from '@/stores/authStore';
import { dataService } from '@/services/dataService';
import { setupTenantMfa, confirmTenantMfa, changeTenantPassword } from '@/services/authApi';
import {
    Edit,
    Lock,
    User,
    Shield,
    ShieldCheck,
    Key,
    RefreshCw,
    Smartphone,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

interface UserCredential {
    userId: string;
    status: 'active' | 'inactive';
    password: {
        defaultPasswordPolicy?: string;
        mustChangeOnFirstLogin?: boolean;
        newPassword?: string;
    };
    mfa: {
        enabled: boolean;
        factors: Array<{
            factorId: string;
            type: string;
            provider: string;
            enrolled: boolean;
            enrolledAt: string | null;
        }>;
    };
}

interface CredentialsData {
    schemaVersion: string;
    tenantId: string;
    generatedAt: string;
    defaults: {
        password: {
            defaultPasswordPolicy: string;
            mustChangeOnFirstLogin: boolean;
        };
        mfa: {
            enabled: boolean;
            defaultFactor: string;
        };
    };
    users: UserCredential[];
}

export const UserAuthenticationPage: React.FC = () => {
    const { user } = useAuthStore();
    const [loading, setLoading] = useState(false);
    const [credentials, setCredentials] = useState<CredentialsData | null>(null);
    const [userModalOpen, setUserModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<UserCredential | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>('');

    // MFA 등록 모달
    const [mfaEnrollModalOpen, setMfaEnrollModalOpen] = useState(false);
    const [mfaEnrollStep, setMfaEnrollStep] = useState<'start' | 'setup' | 'confirm'>('start');
    const [mfaSetupData, setMfaSetupData] = useState<{ secret: string; qrUrl: string; issuer: string } | null>(null);
    const [mfaConfirmCode, setMfaConfirmCode] = useState('');
    const [mfaLoading, setMfaLoading] = useState(false);
    const [mfaError, setMfaError] = useState<string | null>(null);

    // 내 비밀번호 변경 모달
    const [passwordChangeModalOpen, setPasswordChangeModalOpen] = useState(false);
    const [pwCurrent, setPwCurrent] = useState('');
    const [pwNew, setPwNew] = useState('');
    const [pwConfirm, setPwConfirm] = useState('');
    const [pwLoading, setPwLoading] = useState(false);
    const [pwError, setPwError] = useState<string | null>(null);

    // Form states
    const [userForm, setUserForm] = useState<Partial<UserCredential>>({});

    useEffect(() => {
        const currentUser = user;
        if (currentUser?.userType === 'tenant-user') {
            loadCredentials(currentUser.tenantId);
        }
    }, [user]);

    // 최초 로그인 시 비밀번호 변경 유도: URL에 changePassword=1 이면 모달 자동 오픈
    useEffect(() => {
        if (user?.userType !== 'tenant-user') return;
        if (getQueryParam('changePassword') === '1') {
            setPasswordChangeModalOpen(true);
            removeQueryParams(['changePassword']);
        }
    }, [user?.userType]);

    const loadCredentials = async (tenantId: string) => {
        try {
            setLoading(true);
            const data = await dataService.getTenantCredentials(tenantId);
            setCredentials(data);
        } catch (error) {
            console.error('인증 정보를 불러오는데 실패했습니다:', error);
            setCredentials(null);
        } finally {
            setLoading(false);
        }
    };

    const currentUserCred = credentials?.users?.find(
        (u) => user?.userType === 'tenant-user' && u.userId === user.userId
    );
    const isMfaEnrolled = currentUserCred?.mfa?.factors?.some((f) => f.type === 'totp' && f.enrolled) ?? false;

    const handleMfaEnrollStart = async () => {
        setMfaError(null);
        setMfaLoading(true);
        try {
            const data = await setupTenantMfa();
            setMfaSetupData({ secret: data.secret, qrUrl: data.qrUrl, issuer: data.issuer });
            setMfaEnrollStep('setup');
        } catch (err) {
            setMfaError(err instanceof Error ? err.message : 'MFA 설정 시작에 실패했습니다.');
        } finally {
            setMfaLoading(false);
        }
    };

    const handleMfaEnrollConfirm = async () => {
        const code = mfaConfirmCode.replace(/\D/g, '').slice(0, 6);
        if (code.length !== 6) {
            setMfaError('6자리 인증 코드를 입력하세요.');
            return;
        }
        setMfaError(null);
        setMfaLoading(true);
        try {
            await confirmTenantMfa(code);
            setMfaEnrollModalOpen(false);
            setMfaEnrollStep('start');
            setMfaSetupData(null);
            setMfaConfirmCode('');
            if (user?.userType === 'tenant-user') loadCredentials(user.tenantId);
        } catch (err) {
            setMfaError(err instanceof Error ? err.message : '인증 코드가 올바르지 않습니다.');
        } finally {
            setMfaLoading(false);
        }
    };

    const closeMfaEnrollModal = () => {
        setMfaEnrollModalOpen(false);
        setMfaEnrollStep('start');
        setMfaSetupData(null);
        setMfaConfirmCode('');
        setMfaError(null);
    };

    /** 비밀번호 정책 검사 (8자 이상, 대·소문자·숫자·특수문자) */
    const validateNewPassword = (p: string): string | null => {
        if (p.length < 8) return '비밀번호는 8자 이상이어야 합니다.';
        if (!/[A-Z]/.test(p)) return '비밀번호에 대문자를 1자 이상 포함해야 합니다.';
        if (!/[a-z]/.test(p)) return '비밀번호에 소문자를 1자 이상 포함해야 합니다.';
        if (!/\d/.test(p)) return '비밀번호에 숫자를 1자 이상 포함해야 합니다.';
        if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(p)) return '비밀번호에 특수문자를 1자 이상 포함해야 합니다.';
        return null;
    };

    const handlePasswordChangeSubmit = async () => {
        setPwError(null);
        if (!pwCurrent.trim()) {
            setPwError('현재 비밀번호를 입력하세요.');
            return;
        }
        if (!pwNew.trim()) {
            setPwError('새 비밀번호를 입력하세요.');
            return;
        }
        if (pwNew !== pwConfirm) {
            setPwError('새 비밀번호가 일치하지 않습니다.');
            return;
        }
        const err = validateNewPassword(pwNew);
        if (err) {
            setPwError(err);
            return;
        }
        setPwLoading(true);
        try {
            await changeTenantPassword(pwCurrent, pwNew);
            setPasswordChangeModalOpen(false);
            setPwCurrent('');
            setPwNew('');
            setPwConfirm('');
        } catch (e) {
            setPwError(e instanceof Error ? e.message : '비밀번호 변경에 실패했습니다.');
        } finally {
            setPwLoading(false);
        }
    };

    const handleSaveCredentials = async (updatedData: CredentialsData) => {
        const currentUser = user;
        if (currentUser?.userType !== 'tenant-user') return;

        try {
            await dataService.updateTenantCredentials(currentUser.tenantId, {
                defaults: updatedData.defaults,
                users: updatedData.users.map(u => ({
                    userId: u.userId,
                    status: u.status,
                    password: u.password,
                    mfa: u.mfa,
                })),
            });
            setCredentials(updatedData);
            console.log('인증 데이터가 성공적으로 저장되었습니다.');
        } catch (error) {
            console.error('저장에 실패했습니다:', error);
            alert('저장에 실패했습니다. 다시 시도해주세요.');
        }
    };

    const handleUserEdit = (userCred: UserCredential) => {
        setEditingUser(userCred);
        setUserForm({
            userId: userCred.userId,
            status: userCred.status,
            password: {
                defaultPasswordPolicy: userCred.password?.defaultPasswordPolicy,
                mustChangeOnFirstLogin: userCred.password?.mustChangeOnFirstLogin,
            },
            mfa: {
                enabled: userCred.mfa?.enabled || false,
                factors: userCred.mfa?.factors || [],
            },
        });
        setUserModalOpen(true);
    };

    const handleUserSubmit = () => {
        if (!credentials || !editingUser || !userForm.userId) return;

        const updatedUsers = credentials.users.map(u => {
            if (u.userId === editingUser.userId) {
                return {
                    ...u,
                    status: userForm.status || u.status,
                    password: {
                        ...u.password,
                        defaultPasswordPolicy: userForm.password?.defaultPasswordPolicy || u.password?.defaultPasswordPolicy,
                        mustChangeOnFirstLogin: userForm.password?.mustChangeOnFirstLogin !== undefined 
                            ? userForm.password.mustChangeOnFirstLogin 
                            : u.password?.mustChangeOnFirstLogin,
                        ...(userForm.password?.newPassword ? { newPassword: userForm.password.newPassword } : {}),
                    },
                    mfa: {
                        ...u.mfa,
                        enabled: userForm.mfa?.enabled !== undefined ? userForm.mfa.enabled : u.mfa?.enabled || false,
                    },
                };
            }
            return u;
        });

        handleSaveCredentials({
            ...credentials,
            users: updatedUsers,
        });
        setUserModalOpen(false);
        setUserForm({});
    };

    const handlePasswordReset = (userId: string) => {
        if (!confirm(`사용자 "${userId}"의 비밀번호를 초기화하시겠습니까?`)) return;
        
        if (!credentials) return;

        const updatedUsers = credentials.users.map(u => {
            if (u.userId === userId) {
                return {
                    ...u,
                    password: {
                        ...u.password,
                        mustChangeOnFirstLogin: true,
                        newPassword: userId, // 기본 비밀번호를 userId로 설정
                    },
                };
            }
            return u;
        });

        handleSaveCredentials({
            ...credentials,
            users: updatedUsers,
        });
    };

    // 필터링된 사용자 목록 계산
    const filteredUsers = credentials?.users.filter(user => 
        searchQuery === '' || user.userId.toLowerCase().includes(searchQuery.toLowerCase())
    ) || [];

    if (!user || user.userType !== 'tenant-user') {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <p className="text-gray-500">테넌트 사용자로 로그인해주세요.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="tenant-admin-page space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">사용자 인증 관리</h1>
                <p className="text-gray-600 mt-1">
                    사용자의 비밀번호 정책, MFA 설정 등을 관리할 수 있습니다.
                </p>
                <p className="text-sm text-gray-500 mt-1">
                    Tenant에서 2단계 인증이 활성화된 경우 로그인 시 인증 앱 코드 입력이 필요합니다. 미등록 사용자는 아래 &apos;내 2단계 인증 설정&apos;에서 등록할 수 있습니다.
                </p>
            </div>

            {loading ? (
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            ) : credentials ? (
                <>
                    {/* 기본 설정 */}
                    <Card className="shadow-sm">
                        <div className="p-6">
                            <div className="flex items-center gap-2 mb-4">
                                <Shield className="h-5 w-5 text-blue-600" />
                                <h2 className="text-lg font-semibold">기본 설정</h2>
                            </div>
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label>기본 비밀번호 정책</Label>
                                        <p className="text-sm text-gray-500 mt-1">
                                            {credentials.defaults.password.defaultPasswordPolicy}
                                        </p>
                                    </div>
                                    <div>
                                        <Label>첫 로그인 시 비밀번호 변경 필수</Label>
                                        <p className="text-sm text-gray-500 mt-1">
                                            {credentials.defaults.password.mustChangeOnFirstLogin ? '예' : '아니오'}
                                        </p>
                                    </div>
                                    <div>
                                        <Label>기본 MFA 활성화</Label>
                                        <p className="text-sm text-gray-500 mt-1">
                                            {credentials.defaults.mfa.enabled ? '활성화' : '비활성화'}
                                        </p>
                                    </div>
                                    <div>
                                        <Label>기본 MFA 인증 요소</Label>
                                        <p className="text-sm text-gray-500 mt-1">
                                            {credentials.defaults.mfa.defaultFactor}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>

                    {/* 내 2단계 인증 설정 (로그인한 사용자 본인) */}
                    {user?.userType === 'tenant-user' && credentials && (
                        <Card className="shadow-sm">
                            <div className="p-6">
                                <div className="flex items-center gap-2 mb-4">
                                    <Smartphone className="h-5 w-5 text-purple-600" />
                                    <h2 className="text-lg font-semibold">내 2단계 인증 설정</h2>
                                </div>
                                {isMfaEnrolled ? (
                                    <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 border border-green-100">
                                        <ShieldCheck className="h-5 w-5 text-green-600 shrink-0" />
                                        <p className="text-sm text-green-800">이 계정에 2단계 인증(TOTP)이 등록되어 있습니다. 로그인 시 인증 앱 코드 입력이 필요합니다.</p>
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-between gap-4">
                                        <p className="text-sm text-gray-600">인증 앱(Google Authenticator 등)으로 로그인 시 6자리 코드를 사용할 수 있도록 등록합니다.</p>
                                        <Button
                                            variant="default"
                                            size="sm"
                                            onClick={() => setMfaEnrollModalOpen(true)}
                                        >
                                            <Shield className="h-4 w-4 mr-2" />
                                            MFA 등록
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </Card>
                    )}

                    {/* 내 비밀번호 변경 (로그인한 사용자 본인) */}
                    {user?.userType === 'tenant-user' && (
                        <Card className="shadow-sm">
                            <div className="p-6">
                                <div className="flex items-center gap-2 mb-4">
                                    <Key className="h-5 w-5 text-amber-600" />
                                    <h2 className="text-lg font-semibold">내 비밀번호 변경</h2>
                                </div>
                                <p className="text-sm text-gray-600 mb-4">로그인에 사용하는 비밀번호를 변경합니다. 8자 이상, 대문자·소문자·숫자·특수문자를 포함해야 합니다.</p>
                                <Button variant="outline" size="sm" onClick={() => setPasswordChangeModalOpen(true)}>
                                    <Lock className="h-4 w-4 mr-2" />
                                    비밀번호 변경
                                </Button>
                            </div>
                        </Card>
                    )}

                    {/* 사용자 목록 */}
                    <Card className="shadow-sm">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-2">
                                    <User className="h-5 w-5 text-green-600" />
                                    <h2 className="text-lg font-semibold">사용자 인증 설정</h2>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Input
                                        placeholder="사용자 ID 검색..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-64"
                                    />
                                </div>
                            </div>

                            <div className="space-y-3">
                                {filteredUsers.map((userCred) => (
                                    <div
                                        key={userCred.userId}
                                        className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-sm transition-all duration-200"
                                    >
                                        <div className="flex items-center gap-4 flex-1">
                                            <div className="p-2.5 bg-blue-50 rounded-lg border border-blue-100">
                                                <Lock className="h-5 w-5 text-blue-600" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-3 mb-1">
                                                    <h3 className="text-sm font-semibold text-gray-900">{userCred.userId}</h3>
                                                    <Badge
                                                        variant={userCred.status === 'active' ? 'default' : 'secondary'}
                                                        className="text-xs px-2 py-1"
                                                    >
                                                        {userCred.status === 'active' ? '활성' : '비활성'}
                                                    </Badge>
                                                    {userCred.mfa?.enabled && (
                                                        <Badge variant="outline" className="text-xs px-2 py-1">
                                                            <Shield className="h-3 w-3 mr-1" />
                                                            MFA 활성화
                                                        </Badge>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-4 text-xs text-gray-500">
                                                    <span>비밀번호 정책: {userCred.password?.defaultPasswordPolicy || '기본값'}</span>
                                                    {userCred.password?.mustChangeOnFirstLogin && (
                                                        <span className="text-orange-600">첫 로그인 시 변경 필수</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex gap-1.5 ml-4">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="h-8 px-3 hover:bg-blue-50 hover:border-blue-200"
                                                onClick={() => handlePasswordReset(userCred.userId)}
                                            >
                                                <Key className="h-3.5 w-3.5 mr-1" />
                                                비밀번호 초기화
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="h-8 w-8 p-0 hover:bg-blue-50 hover:border-blue-200"
                                                onClick={() => handleUserEdit(userCred)}
                                            >
                                                <Edit className="h-3.5 w-3.5" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                                {filteredUsers.length === 0 && (
                                    <div className="text-center py-12 text-gray-500">
                                        <User className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                                        <p className="text-sm">검색 결과가 없습니다.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card>

                    {/* 사용자 편집 모달 */}
                    <Dialog open={userModalOpen} onOpenChange={setUserModalOpen}>
                        <DialogContent className="max-w-md">
                            <DialogHeader>
                                <DialogTitle>사용자 인증 설정 수정</DialogTitle>
                                <DialogDescription>
                                    사용자 "{editingUser?.userId}"의 인증 설정을 수정합니다.
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4">
                                <div>
                                    <Label htmlFor="status">상태</Label>
                                    <Select
                                        value={userForm.status || 'active'}
                                        onValueChange={(value) => setUserForm({ ...userForm, status: value as 'active' | 'inactive' })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white border border-gray-200 shadow-lg z-50">
                                            <SelectItem value="active">활성</SelectItem>
                                            <SelectItem value="inactive">비활성</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label htmlFor="passwordPolicy">비밀번호 정책</Label>
                                    <Select
                                        value={userForm.password?.defaultPasswordPolicy || 'userId'}
                                        onValueChange={(value) => setUserForm({
                                            ...userForm,
                                            password: { ...userForm.password, defaultPasswordPolicy: value },
                                        })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white border border-gray-200 shadow-lg z-50">
                                            <SelectItem value="userId">userId</SelectItem>
                                            <SelectItem value="custom">custom</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        id="mustChange"
                                        checked={userForm.password?.mustChangeOnFirstLogin || false}
                                        onCheckedChange={(checked) => setUserForm({
                                            ...userForm,
                                            password: { ...userForm.password, mustChangeOnFirstLogin: checked as boolean },
                                        })}
                                    />
                                    <Label htmlFor="mustChange" className="cursor-pointer">첫 로그인 시 비밀번호 변경 필수</Label>
                                </div>
                                <div>
                                    <Label htmlFor="newPassword">새 비밀번호 (선택사항)</Label>
                                    <Input
                                        id="newPassword"
                                        type="password"
                                        placeholder="비밀번호를 변경하려면 입력하세요"
                                        value={(userForm.password as any)?.newPassword || ''}
                                        onChange={(e) => setUserForm({
                                            ...userForm,
                                            password: { ...userForm.password, newPassword: e.target.value },
                                        })}
                                    />
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        id="mfaEnabled"
                                        checked={userForm.mfa?.enabled || false}
                                        onCheckedChange={(checked) => setUserForm({
                                            ...userForm,
                                            mfa: { factors: userForm.mfa?.factors || [], ...userForm.mfa, enabled: checked as boolean },
                                        })}
                                    />
                                    <Label htmlFor="mfaEnabled" className="cursor-pointer">MFA 활성화</Label>
                                </div>
                                <div className="flex justify-end gap-2">
                                    <Button variant="outline" onClick={() => setUserModalOpen(false)}>
                                        취소
                                    </Button>
                                    <Button onClick={handleUserSubmit}>
                                        저장
                                    </Button>
                                </div>
                            </div>
                        </DialogContent>
                    </Dialog>

                    {/* MFA 등록 모달 */}
                    <Dialog open={mfaEnrollModalOpen} onOpenChange={(open) => !open && closeMfaEnrollModal()}>
                        <DialogContent className="max-w-md">
                            <DialogHeader>
                                <DialogTitle>2단계 인증(TOTP) 등록</DialogTitle>
                                <DialogDescription>
                                    {mfaEnrollStep === 'start' && '인증 앱에 계정을 추가하려면 설정을 시작하세요.'}
                                    {mfaEnrollStep === 'setup' && '아래 비밀 키를 인증 앱에 수동 입력하거나, otpauth 링크를 사용하세요.'}
                                    {mfaEnrollStep === 'confirm' && '인증 앱에 표시된 6자리 코드를 입력하세요.'}
                                </DialogDescription>
                            </DialogHeader>
                            {mfaError && (
                                <div className="p-3 rounded-lg bg-red-50 border border-red-100 text-sm text-red-700">
                                    {mfaError}
                                </div>
                            )}
                            {mfaEnrollStep === 'start' && (
                                <div className="space-y-4">
                                    <p className="text-sm text-gray-600">Google Authenticator, Microsoft Authenticator 등 TOTP 지원 앱을 준비한 뒤 아래 버튼을 누르세요.</p>
                                    <Button
                                        className="w-full"
                                        onClick={handleMfaEnrollStart}
                                        disabled={mfaLoading}
                                    >
                                        {mfaLoading ? '설정 중...' : '설정 시작'}
                                    </Button>
                                </div>
                            )}
                            {mfaEnrollStep === 'setup' && mfaSetupData && (
                                <div className="space-y-4">
                                    <div className="flex flex-col items-center">
                                        <Label className="text-xs text-gray-500 mb-2">QR 코드 (인증 앱으로 스캔)</Label>
                                        <div className="p-3 rounded-lg bg-white border border-gray-200 inline-block">
                                            <QRCodeSVG value={mfaSetupData.qrUrl} size={200} level="M" />
                                        </div>
                                        <p className="text-xs text-gray-500 mt-2 text-center">Google Authenticator, Microsoft Authenticator 등에서 QR 코드 스캔을 선택하세요.</p>
                                    </div>
                                    <div>
                                        <Label className="text-xs text-gray-500">비밀 키 (수동 입력용)</Label>
                                        <div className="mt-1 p-3 rounded-lg bg-gray-50 border font-mono text-sm break-all select-all">
                                            {mfaSetupData.secret}
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">인증 앱에서 &quot;키 입력&quot; 또는 &quot;수동 입력&quot;을 선택한 뒤 위 키를 입력하세요.</p>
                                    </div>
                                    <div>
                                        <Label className="text-xs text-gray-500">otpauth 링크 (QR 스캔이 안 될 때)</Label>
                                        <a
                                            href={mfaSetupData.qrUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="mt-1 block p-2 rounded border text-xs text-primary underline break-all"
                                        >
                                            {mfaSetupData.qrUrl}
                                        </a>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button variant="outline" className="flex-1" onClick={closeMfaEnrollModal}>
                                            취소
                                        </Button>
                                        <Button className="flex-1" onClick={() => setMfaEnrollStep('confirm')}>
                                            다음
                                        </Button>
                                    </div>
                                </div>
                            )}
                            {mfaEnrollStep === 'confirm' && (
                                <div className="space-y-4">
                                    <div>
                                        <Label htmlFor="mfaConfirmCode">인증 코드 (6자리)</Label>
                                        <Input
                                            id="mfaConfirmCode"
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={6}
                                            placeholder="000000"
                                            value={mfaConfirmCode}
                                            onChange={(e) => setMfaConfirmCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                            disabled={mfaLoading}
                                            className="mt-1 text-center text-lg tracking-widest font-mono"
                                        />
                                    </div>
                                    <div className="flex gap-2">
                                        <Button variant="outline" className="flex-1" onClick={() => setMfaEnrollStep('setup')}>
                                            뒤로
                                        </Button>
                                        <Button
                                            className="flex-1"
                                            onClick={handleMfaEnrollConfirm}
                                            disabled={mfaLoading || mfaConfirmCode.replace(/\D/g, '').length !== 6}
                                        >
                                            {mfaLoading ? '등록 중...' : '등록 완료'}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </DialogContent>
                    </Dialog>

                    {/* 내 비밀번호 변경 모달 */}
                    <Dialog open={passwordChangeModalOpen} onOpenChange={setPasswordChangeModalOpen}>
                        <DialogContent className="max-w-md">
                            <DialogHeader>
                                <DialogTitle>내 비밀번호 변경</DialogTitle>
                                <DialogDescription>
                                    현재 비밀번호를 입력한 뒤 새 비밀번호를 설정하세요.
                                </DialogDescription>
                            </DialogHeader>
                            {pwError && (
                                <div className="p-3 rounded-lg bg-red-50 border border-red-100 text-sm text-red-700">
                                    {pwError}
                                </div>
                            )}
                            <div className="space-y-4">
                                <div>
                                    <Label htmlFor="pwCurrent">현재 비밀번호</Label>
                                    <Input
                                        id="pwCurrent"
                                        type="password"
                                        value={pwCurrent}
                                        onChange={(e) => setPwCurrent(e.target.value)}
                                        disabled={pwLoading}
                                        placeholder="현재 비밀번호"
                                        className="mt-1"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="pwNew">새 비밀번호</Label>
                                    <Input
                                        id="pwNew"
                                        type="password"
                                        value={pwNew}
                                        onChange={(e) => setPwNew(e.target.value)}
                                        disabled={pwLoading}
                                        placeholder="새 비밀번호"
                                        className="mt-1"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">8자 이상, 대문자·소문자·숫자·특수문자 각 1자 이상</p>
                                </div>
                                <div>
                                    <Label htmlFor="pwConfirm">새 비밀번호 확인</Label>
                                    <Input
                                        id="pwConfirm"
                                        type="password"
                                        value={pwConfirm}
                                        onChange={(e) => setPwConfirm(e.target.value)}
                                        disabled={pwLoading}
                                        placeholder="새 비밀번호 다시 입력"
                                        className="mt-1"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 pt-2">
                                <Button variant="outline" onClick={() => setPasswordChangeModalOpen(false)} disabled={pwLoading}>
                                    취소
                                </Button>
                                <Button onClick={handlePasswordChangeSubmit} disabled={pwLoading}>
                                    {pwLoading ? '변경 중...' : '변경'}
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>
                </>
            ) : (
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <p className="text-gray-500">인증 정보를 불러올 수 없습니다.</p>
                        <Button
                            variant="outline"
                            className="mt-4"
                            onClick={() => user?.userType === 'tenant-user' && loadCredentials(user.tenantId)}
                        >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            다시 시도
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

UserAuthenticationPage.displayName = 'UserAuthenticationPage';
